import { useEffect, useRef, useState } from 'react';
import type { BookingPurchaseDialogData } from './BookingPurchaseDialog.types';
import type { BookEventHostData, BookEventUser } from './bookEvent.types';
import {
  isAllowToBook,
  isAppointmentAllowToBook,
  isGoingToWaitingList,
  isUserFirstBook,
  isUserLoggedIn,
} from './bookEventRules';
import {
  bookingAnalytics,
  isBookAppointment,
  type BookingOrchestrationVariant,
} from './bookingDialogVariant';
import { useBookEventActions } from './useBookEventActions';
import { useBookEventEagreements } from './useBookEventEagreements';
import { useBookEventPricing } from './useBookEventPricing';
import { useBookEventProjection } from './useBookEventProjection';

function resolveAllowToBook(
  data: BookEventHostData,
  variant: BookingOrchestrationVariant,
  priceToPay: number | null
): boolean {
  if (isBookAppointment(variant)) {
    return isAppointmentAllowToBook(data.branch, priceToPay);
  }
  return isAllowToBook(data.event, data.branch, priceToPay, data.portalConfig);
}

export function useBookingPurchaseDialog(
  data: BookEventHostData,
  variant: BookingOrchestrationVariant = 'book-event'
): BookingPurchaseDialogData {
  const [user, setUser] = useState<BookEventUser | null>(data.user);
  const didTrackInitiation = useRef(false);

  useEffect(() => {
    setUser(data.user);
  }, [data.user]);

  const pricing = useBookEventPricing({ data, user, setUser });
  const agreements = useBookEventEagreements(data);
  const actions = useBookEventActions({
    data,
    variant,
    user,
    guests: pricing.guests,
    priceToPay: pricing.priceToPay,
    creditsToPay: pricing.creditsToPay,
    selectedPayment: pricing.selectedPayment,
    collectPaymentCard: pricing.collectPaymentCard,
    refreshEagreements: agreements.refreshEagreements,
  });

  const loggedIn = isUserLoggedIn(user);
  const userHasCredits = pricing.creditsToPay > 0;
  const goingToWaitingList = isGoingToWaitingList(data.event, data.branch);
  const allowToBook = resolveAllowToBook(data, variant, pricing.priceToPay);

  useEffect(() => {
    if (didTrackInitiation.current) {
      return;
    }
    didTrackInitiation.current = true;
    data.trackCheckoutInitiation();
    const analytics = bookingAnalytics(variant);
    data.trackEvent({
      category: analytics.category,
      name: loggedIn ? analytics.showLoggedIn : analytics.showLoggedOut,
      ...(analytics.includeEventMetadata ? { metadata: { eventName: data.event.name } } : {}),
    });
    data.scrollIframeToTop();
    pricing.initializePrice();
    void agreements.refreshEagreements();
  }, [agreements.refreshEagreements, data, loggedIn, pricing.initializePrice, variant]);

  return useBookEventProjection({
    data,
    variant,
    user,
    loggedIn,
    allowToBook,
    userHasCredits,
    goingToWaitingList,
    firstBook: isUserFirstBook(user),
    ...pricing,
    ...agreements,
    ...actions,
  });
}
